import { storeImage } from './storage';
import { setAssetUrl } from './assetUpload';

const PRESENTING_KEY = 'presenting';
const PRESENTING_SIZE = 10;

const GRID_COLS = 24;
const GRID_ROWS = 30;

const DAGGERHEART_TOKEN_FIELDS = [
  'dhHpMax',
  'dhHpUsed',
  'dhStressMax',
  'dhStressUsed',
  'dhExtra1Label',
  'dhExtra1Max',
  'dhExtra1Used',
  'dhExtra2Label',
  'dhExtra2Max',
  'dhExtra2Used',
  'dhThresholdMajor',
  'dhThresholdSevere',
];

const getDaggerheartTokenFields = (token) =>
  DAGGERHEART_TOKEN_FIELDS.reduce((fields, key) => {
    if (token[key] !== undefined) fields[key] = token[key];
    return fields;
  }, {});

const BATTLE_EFFECT_PRESETS = {
  fog: { name: 'Fog', color: '#9fb3c8', opacity: 0.45 },
  ice: { name: 'Ice', color: '#9ad8ff', opacity: 0.42 },
  fire: { name: 'Fire', color: '#ffb38a', opacity: 0.38 },
  acid: { name: 'Acid', color: '#b6e673', opacity: 0.36 },
};

const clampEffectOpacity = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0.4;
  return Math.min(0.9, Math.max(0.05, num));
};

const VALID_EFFECT_SHAPES = [
  'line_h', 'line_v',
  'line_n', 'line_s', 'line_e', 'line_w',
  'line_ne', 'line_nw', 'line_se', 'line_sw',
  'cone_right', 'cone_left', 'cone_up', 'cone_down',
  'circle', 'square',
  // Legacy aliases kept for backwards compatibility
  'line', 'cone',
];

const clampEffectSize = (value) => {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num)) return 3;
  return Math.min(10, Math.max(1, num));
};

const generateShapeCells = (shape, size, cx, cy) => {
  const cells = [];
  const inBounds = (x, y) => x >= 0 && x < GRID_COLS && y >= 0 && y < GRID_ROWS;

  switch (shape) {
    // ── Lines: bidirectional ──────────────────────────────────────────────
    case 'line':   // legacy alias for line_h
    case 'line_h': {
      for (let i = -size; i <= size; i++) {
        const x = cx + i;
        if (inBounds(x, cy)) cells.push({ x, y: cy });
      }
      break;
    }
    case 'line_v': {
      for (let i = -size; i <= size; i++) {
        const y = cy + i;
        if (inBounds(cx, y)) cells.push({ x: cx, y });
      }
      break;
    }
    // ── Lines: cardinal directions (one-way from centre) ──────────────────
    case 'line_n': {
      for (let i = 0; i <= size; i++) {
        const y = cy - i;
        if (inBounds(cx, y)) cells.push({ x: cx, y });
      }
      break;
    }
    case 'line_s': {
      for (let i = 0; i <= size; i++) {
        const y = cy + i;
        if (inBounds(cx, y)) cells.push({ x: cx, y });
      }
      break;
    }
    case 'line_e': {
      for (let i = 0; i <= size; i++) {
        const x = cx + i;
        if (inBounds(x, cy)) cells.push({ x, y: cy });
      }
      break;
    }
    case 'line_w': {
      for (let i = 0; i <= size; i++) {
        const x = cx - i;
        if (inBounds(x, cy)) cells.push({ x, y: cy });
      }
      break;
    }
    // ── Lines: diagonal directions (one-way from centre) ─────────────────
    case 'line_ne': {
      for (let i = 0; i <= size; i++) {
        const x = cx + i;
        const y = cy - i;
        if (inBounds(x, y)) cells.push({ x, y });
      }
      break;
    }
    case 'line_nw': {
      for (let i = 0; i <= size; i++) {
        const x = cx - i;
        const y = cy - i;
        if (inBounds(x, y)) cells.push({ x, y });
      }
      break;
    }
    case 'line_se': {
      for (let i = 0; i <= size; i++) {
        const x = cx + i;
        const y = cy + i;
        if (inBounds(x, y)) cells.push({ x, y });
      }
      break;
    }
    case 'line_sw': {
      for (let i = 0; i <= size; i++) {
        const x = cx - i;
        const y = cy + i;
        if (inBounds(x, y)) cells.push({ x, y });
      }
      break;
    }
    // ── Cones ─────────────────────────────────────────────────────────────
    case 'cone':        // legacy alias for cone_right
    case 'cone_right': {
      for (let d = 0; d <= size; d++) {
        for (let w = -d; w <= d; w++) {
          const x = cx + d;
          const y = cy + w;
          if (inBounds(x, y)) cells.push({ x, y });
        }
      }
      break;
    }
    case 'cone_left': {
      for (let d = 0; d <= size; d++) {
        for (let w = -d; w <= d; w++) {
          const x = cx - d;
          const y = cy + w;
          if (inBounds(x, y)) cells.push({ x, y });
        }
      }
      break;
    }
    case 'cone_up': {
      for (let d = 0; d <= size; d++) {
        for (let w = -d; w <= d; w++) {
          const x = cx + w;
          const y = cy - d;
          if (inBounds(x, y)) cells.push({ x, y });
        }
      }
      break;
    }
    case 'cone_down': {
      for (let d = 0; d <= size; d++) {
        for (let w = -d; w <= d; w++) {
          const x = cx + w;
          const y = cy + d;
          if (inBounds(x, y)) cells.push({ x, y });
        }
      }
      break;
    }
    // ── Area shapes ───────────────────────────────────────────────────────
    case 'circle': {
      for (let dx = -size; dx <= size; dx++) {
        for (let dy = -size; dy <= size; dy++) {
          if (Math.sqrt(dx * dx + dy * dy) <= size) {
            const x = cx + dx;
            const y = cy + dy;
            if (inBounds(x, y)) cells.push({ x, y });
          }
        }
      }
      break;
    }
    case 'square': {
      for (let dx = -size; dx <= size; dx++) {
        for (let dy = -size; dy <= size; dy++) {
          const x = cx + dx;
          const y = cy + dy;
          if (inBounds(x, y)) cells.push({ x, y });
        }
      }
      break;
    }
    default:
      break;
  }
  return cells;
};

const buildBattleEffectDefaults = (kind = 'fog') => {
  const preset = BATTLE_EFFECT_PRESETS[kind] || BATTLE_EFFECT_PRESETS.fog;
  return {
    id: null,
    name: preset.name,
    kind,
    color: preset.color,
    opacity: preset.opacity,
    visible: true,
    group: '',
    shape: '',
    size: 3,
    cells: [],
  };
};

const isPresentingSizeValue = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === PRESENTING_KEY;
  }
  return Number(value) === PRESENTING_SIZE;
};

const clampStandardSizeValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 1;
  return Math.min(4, Math.max(1, num));
};

const HEX_SIZE_GRID_UNITS = { hex1: 1, hex3: 2, hex7: 3, hex12: 4 };

const getGridUnitsForSize = (value) => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower in HEX_SIZE_GRID_UNITS) return HEX_SIZE_GRID_UNITS[lower];
    if (lower === PRESENTING_KEY) return PRESENTING_SIZE;
  }
  if (isPresentingSizeValue(value)) return PRESENTING_SIZE;
  return clampStandardSizeValue(value);
};

export const placeTokensAtBottomRight = (existingTokens, tokensToPlace, gridConfig) => {
  const cols = Math.max(1, Math.floor(Number(gridConfig?.cols) || GRID_COLS));
  const rows = Math.max(1, Math.floor(Number(gridConfig?.rows) || GRID_ROWS));
  const isHex = gridConfig?.type === 'hex';
  const sqrt3 = Math.sqrt(3);
  const boardWidth = isHex ? 1.5 * cols + 0.5 : cols;
  const boardHeight = isHex ? sqrt3 * (rows + 0.5) : rows;
  const toBounds = (token) => {
    const size = getGridUnitsForSize(token?.size);
    const xValue = Number(token?.x);
    const yValue = Number(token?.y);
    const x = Number.isFinite(xValue) ? xValue : 0;
    const y = Number.isFinite(yValue) ? yValue : 0;
    if (isHex) {
      const centerX = 1 + x * 1.5;
      const centerY = sqrt3 / 2 + y * sqrt3 + (x % 2 === 1 ? sqrt3 / 2 : 0);
      return {
        x: centerX - size,
        y: centerY - (size * sqrt3) / 2,
        width: size * 2,
        height: size * sqrt3,
      };
    }
    return {
      x,
      y,
      width: size,
      height: size,
    };
  };
  const overlaps = (first, second) => (
    first.x < second.x + second.width
    && first.x + first.width > second.x
    && first.y < second.y + second.height
    && first.y + first.height > second.y
  );
  const fitsBoard = (bounds) => (
    bounds.x >= 0
    && bounds.y >= 0
    && bounds.x + bounds.width <= boardWidth
    && bounds.y + bounds.height <= boardHeight
  );

  const occupied = (existingTokens || []).map(toBounds);
  const placedTokens = [];
  const unplacedTokens = [];

  (tokensToPlace || []).forEach((token) => {
    const size = getGridUnitsForSize(token?.size);
    if ((!isHex && (size > cols || size > rows)) || (isHex && size > rows)) {
      unplacedTokens.push(token);
      return;
    }
    const maxX = isHex
      ? Math.min(cols - 1, Math.floor((boardWidth - size - 1) / 1.5))
      : cols - size;
    const maxY = rows - 1;
    if (maxX < 0) {
      unplacedTokens.push(token);
      return;
    }
    const rightHalfMinX = Math.min(maxX, Math.floor(cols / 2));
    const findPosition = (minX, maxXInRange) => {
      for (let y = maxY; y >= 0; y -= 1) {
        for (let x = maxXInRange; x >= minX; x -= 1) {
          const candidate = toBounds({ x, y, size });
          if (fitsBoard(candidate) && !occupied.some((bounds) => overlaps(candidate, bounds))) {
            return { x, y };
          }
        }
      }
      return null;
    };

    const position = findPosition(rightHalfMinX, maxX);
    if (!position) {
      unplacedTokens.push(token);
      return;
    }

    const placedToken = { ...token, ...position };
    placedTokens.push(placedToken);
    occupied.push(toBounds(placedToken));
  });

  return { placedTokens, unplacedTokens };
};

const DUPLICATE_NAME_INDICATORS = [...'0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ', '@'];

export const getDuplicateTokenNames = (sourceName, existingTokens, count) => {
  const copyCountValue = Number(count);
  const copyCount = Number.isFinite(copyCountValue)
    ? Math.max(0, Math.floor(copyCountValue))
    : 0;
  const name = String(sourceName ?? '').trim();
  const match = name.match(/^(\S)\s+(.+)$/);
  if (!match) return Array(copyCount).fill(name);

  const indicator = match[1].toUpperCase();
  const suffix = match[2];
  const indicatorIndex = DUPLICATE_NAME_INDICATORS.indexOf(indicator);
  let sequenceIndex = indicatorIndex < 0 ? 0 : indicatorIndex + 1;
  const occupiedNames = new Set(
    (existingTokens || []).map((token) => String(token?.name ?? '').trim().toLowerCase())
  );
  const names = [];

  while (names.length < copyCount) {
    const nextIndicator = sequenceIndex < DUPLICATE_NAME_INDICATORS.length
      ? DUPLICATE_NAME_INDICATORS[sequenceIndex++]
      : '@';
    const candidate = `${nextIndicator} ${suffix}`;
    const normalizedCandidate = candidate.toLowerCase();
    if (nextIndicator !== '@' && occupiedNames.has(normalizedCandidate)) continue;
    names.push(candidate);
    occupiedNames.add(normalizedCandidate);
  }

  return names;
};

const getStoredSizeValue = (value) => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower in HEX_SIZE_GRID_UNITS) return lower;
    if (lower === PRESENTING_KEY) return PRESENTING_KEY;
  }
  return isPresentingSizeValue(value) ? PRESENTING_KEY : clampStandardSizeValue(value);
};

const buildPlayerScenePayload = (scene) => {
  if (!scene) return null;
  const visibleTokens = (scene.tokens || [])
    .filter((token) => token && token.visible !== false)
    .map((token) => ({
      id: token.id,
      name: token.name || '',
      x: Number(token.x) || 0,
      y: Number(token.y) || 0,
      size: token.size,
      grayscale: !!token.grayscale,
      conditions: Array.isArray(token.conditions) ? token.conditions : [],
      ...getDaggerheartTokenFields(token),
    }));

  return {
    id: scene.id || null,
    name: scene.name || '',
    ttrpgSystem: scene.ttrpgSystem || null,
    tokens: visibleTokens,
  };
};

const nudgeTokenOnScene = (scene, tokenId, dx, dy, gridCols = 24, gridRows = 30, gridType = 'square') => {
  if (!scene) return null;
  const token = (scene.tokens || []).find((item) => item.id === tokenId);
  if (!token) return null;
  const tokenSize = getGridUnitsForSize(token.size);

  const clampAxis = (value, axisLimit) => {
    const max = axisLimit - (gridType === 'hex' ? 1 : tokenSize);
    return Math.min(max, Math.max(0, value));
  };

  const nextX = clampAxis((token.x ?? 0) + dx, gridCols);
  const nextY = clampAxis((token.y ?? 0) + dy, gridRows);
  if (token.x === nextX && token.y === nextY) return null;

  return {
    ...scene,
    tokens: scene.tokens.map((t) => (t.id === tokenId ? { ...t, x: nextX, y: nextY } : t)),
  };
};

const GM_THEME = { accent: '#e94f37', background: '#26292C', backgroundSoft: '#393e41' };

const TRACKER_ANNOUNCE_ID = (id) => `tracker-announce-${id}`;

const TRACKER_ANNOUNCE_STYLE = {
  background: GM_THEME.backgroundSoft,
  color: '#fff',
  border: `2px solid ${GM_THEME.accent}`,
};

const buildTrackerHistoryRecord = (tracker, changeInfo, gmLabel = 'Game Master') => {
  if (!tracker || !changeInfo) {
    return null;
  }

  const delta = Number(changeInfo.newValue) - Number(changeInfo.oldValue);
  if (!Number.isFinite(delta) || delta === 0) return null;

  const isFear = !!tracker.locked;
  if (!isFear && tracker.visible === false) return null;

  const absDelta = Math.abs(delta);
  const trackerName = isFear
    ? 'Fear'
    : (String(tracker.name || 'Tracker').trim() || 'Tracker');

  const displayText = isFear
    ? (delta > 0
        ? (absDelta === 1
            ? 'The Game Master gains a Fear'
            : `The Game Master gains ${absDelta} Fear`)
        : (absDelta === 1
            ? 'The Game Master spends a Fear'
            : `The Game Master spends ${absDelta} Fear`))
      : (delta > 0
        ? (absDelta === 1
          ? `Raised ${trackerName} by one stage`
          : `Raised ${trackerName} by ${absDelta} stages`)
        : (absDelta === 1
          ? `Lowered ${trackerName} by one stage`
          : `Lowered ${trackerName} by ${absDelta} stages`));

  const command = isFear
    ? `FEAR ${delta > 0 ? '+' : '-'}${absDelta}`
      : `TRACKER ${trackerName} ${delta > 0 ? '+' : '-'}${absDelta}`;

  return {
    username: gmLabel,
    command,
    kind: 'tracker',
    displayText,
    tracker: {
      id: tracker.id,
      name: trackerName,
      size: tracker.size,
      value: tracker.value,
      locked: isFear,
      color: tracker.color || '#e94f37',
      visible: tracker.visible !== false,
    },
    changeInfo: {
      oldValue: changeInfo.oldValue,
      newValue: changeInfo.newValue,
    },
    data: {
      trackerId: tracker.id,
      trackerName,
      trackerSize: tracker.size,
      trackerColor: tracker.color || '#e94f37',
      oldValue: changeInfo.oldValue,
      newValue: changeInfo.newValue,
    },
    theme: {
      accent: GM_THEME.accent,
      background: GM_THEME.background,
    },
  };
};

// ── Shared image-loading helpers ───────────────────────────────────────────
const IMPORT_CONCURRENCY = 4;
const IMPORT_RETRIES = 3;

const importDelay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

async function mapWithConcurrency(items, limit, mapper) {
  const source = Array.isArray(items) ? items : [];
  if (!source.length) return [];
  const results = new Array(source.length);
  let cursor = 0;
  const worker = async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= source.length) return;
      results[index] = await mapper(source[index], index);
    }
  };
  const workerCount = Math.max(1, Math.min(limit, source.length));
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}

async function withRetries(task, label) {
  let lastError = null;
  for (let attempt = 1; attempt <= IMPORT_RETRIES; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (attempt < IMPORT_RETRIES) await importDelay(120 * attempt);
    }
  }
  throw new Error(`${label}: ${lastError?.message || 'Unknown error'}`);
}

async function fetchDataUrlFromRemote(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status} fetching ${url}`);
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function resolveImageId({ dataOrNull, urlOrNull, label }) {
  const hasUrl = !!urlOrNull;
  const hasData = !!dataOrNull;
  if (!hasUrl && !hasData) return null;

  if (hasUrl) {
    try {
      const dataUrl = await withRetries(
        () => fetchDataUrlFromRemote(urlOrNull),
        `${label} (download)`
      );
      const id = await withRetries(() => storeImage(dataUrl), `${label} (store)`);
      setAssetUrl(id, urlOrNull);
      return id;
    } catch (error) {
      if (!hasData) throw error;
      console.warn('Remote image import failed, trying embedded fallback', label, error);
    }
  }

  if (hasData) {
    return withRetries(() => storeImage(dataOrNull), `${label} (embedded store)`);
  }

  return null;
}

/**
 * Process a v2 gameplay payload (from file or Firestore) into a local scene object.
 * Downloads remote image URLs and stores them in IndexedDB.
 * @param {object} payload - v2 scene payload
 * @param {string} sceneId - ID to assign to the scene (preserved from payload.id if available)
 * @param {object} [baseScene] - optional existing scene to merge into (for single-scene import)
 * @returns {object} local scene object with imageId references
 */
async function processScenePayload(payload, sceneId, baseScene = null) {
  const now = Date.now();

  const layers = await mapWithConcurrency(
    payload.layers || [],
    IMPORT_CONCURRENCY,
    async (layer, index) => {
      const imageId = await resolveImageId({
        dataOrNull: layer.imageData || null,
        urlOrNull: layer.imageUrl || null,
        label: `Layer ${index + 1}`,
      });
      return {
        id: `layer_${now}_${sceneId}_${index}`,
        name: layer.name || `Layer ${index + 1}`,
        group: layer.group || '',
        z: Number(layer.z) || 0,
        bgColor: layer.bgColor || '',
        imageId,
        visible: layer.visible !== false,
        centerOnShow: !!layer.centerOnShow,
      };
    }
  );

  const tokens = await mapWithConcurrency(
    payload.tokens || [],
    IMPORT_CONCURRENCY,
    async (token, index) => {
      const goodImageId = await resolveImageId({
        dataOrNull: token.goodImageData || token.imageData || null,
        urlOrNull: token.goodImageUrl || null,
        label: `Token ${index + 1} (good)`,
      });
      const evilImageId = await resolveImageId({
        dataOrNull: token.evilImageData || token.altImageData || null,
        urlOrNull: token.evilImageUrl || null,
        label: `Token ${index + 1} (evil)`,
      });
      const neutralImageId = await resolveImageId({
        dataOrNull: token.neutralImageData || null,
        urlOrNull: token.neutralImageUrl || null,
        label: `Token ${index + 1} (neutral)`,
      });
      const size = getStoredSizeValue(token.size);
      return {
        id: `tok_${now}_${sceneId}_${index}`,
        name: token.name || `Token ${index + 1}`,
        group: token.group || '',
        x: Number.isFinite(token.x) ? token.x : 0,
        y: Number.isFinite(token.y) ? token.y : 0,
        grayscale: !!token.grayscale,
        visible: token.visible !== false,
        imageId: goodImageId,
        altImageId: evilImageId,
        goodImageId,
        evilImageId,
        neutralImageId,
        disposition: token.disposition || 'good',
        showInitial: token.showInitial !== false,
        size,
      };
    }
  );

  const battleEffects = (payload.battleEffects || []).map((effect, index) => {
    const base = buildBattleEffectDefaults(effect.kind || 'fog');
    return {
      id: `bfx_${now}_${sceneId}_${index}`,
      name: effect.name || base.name,
      kind: effect.kind || 'fog',
      color: effect.color || base.color,
      opacity: clampEffectOpacity(effect.opacity ?? base.opacity),
      visible: effect.visible !== false,
      group: effect.group || '',
      cells: Array.isArray(effect.cells)
        ? effect.cells.map((cell) => ({
            x: Math.min(23, Math.max(0, Math.round(Number(cell.x) || 0))),
            y: Math.min(29, Math.max(0, Math.round(Number(cell.y) || 0))),
          }))
        : [],
    };
  });

  const effectFromPayload = payload.effect?.type
    ? { type: payload.effect.type }
    : payload.effect
    ? { type: payload.effect }
    : null;

  return {
    ...(baseScene || {}),
    id: sceneId,
    name: payload.name || (baseScene?.name) || 'Imported Scene',
    layers,
    tokens,
    battleEffects,
    thumb: baseScene?.thumb || null,
    effect: effectFromPayload !== null ? effectFromPayload : (baseScene?.effect || null),
    ttrpgSystem: payload.ttrpgSystem || (baseScene?.ttrpgSystem) || null,
  };
}


export {
  PRESENTING_KEY,
  PRESENTING_SIZE,
  GRID_COLS,
  GRID_ROWS,
  clampEffectOpacity,
  VALID_EFFECT_SHAPES,
  clampEffectSize,
  generateShapeCells,
  buildBattleEffectDefaults,
  getGridUnitsForSize,
  getStoredSizeValue,
  getDaggerheartTokenFields,
  buildPlayerScenePayload,
  nudgeTokenOnScene,
  GM_THEME,
  TRACKER_ANNOUNCE_ID,
  TRACKER_ANNOUNCE_STYLE,
  buildTrackerHistoryRecord,
  processScenePayload,
};
